/**
 * Help search — the vocabulary bridge.
 *
 * The help topics are written in portal vocabulary: "weekly rollup",
 * "submission", "equip. markup", "activity code". A PM at a desk types what he
 * is thinking: "money looks wrong", "send it to hoffman", "hammer hours". A
 * full-text index alone cannot cross that gap, because the words simply are not
 * in the corpus. This map is the crossing.
 *
 * TWO USES, ONE TABLE.
 *  1. QUERY EXPANSION (`expandTerms`). Before the query reaches the index, each
 *     word — and each two/three-word phrase — is looked up here and its targets
 *     are added to the search as extra terms. The literal words are always kept
 *     and always outscore the expansions; an expansion can only ever ADD a
 *     result, never displace an exact hit. That rule is what makes it safe to
 *     be generous below.
 *  2. THE `aliases` DOCUMENT FIELD (`aliasesForText`). The same table read
 *     backwards: a section whose text says "total" is indexed as also being
 *     about "money". That gives the index a real, boostable field to match
 *     rather than relying on expansion alone, and it means a one-word
 *     colloquial query lands on the right section instead of on whichever
 *     section happens to mention the expanded word most often.
 *
 * HOW TO GROW IT. When a real query misses, add the word the user actually
 * typed as a KEY and point it at words the topics genuinely use. Verify the
 * target words exist in the corpus — an alias to a word nobody wrote is dead
 * weight. Then add the query to the fixture table in `helpSearch.test.ts` so it
 * stays landed.
 *
 * Keys and targets are normalized: lowercase, single-spaced, no punctuation.
 * Multi-word keys are matched as phrases in the query before single words are.
 */

/**
 * Words too common to carry meaning. Dropped from the QUERY only — the index
 * keeps them, so a query that is nothing but stop words ("how do I") can still
 * fall back to matching them rather than returning a blank page.
 */
export const HELP_STOP_WORDS: ReadonlySet<string> = new Set([
  'a',
  'about',
  'am',
  'an',
  'and',
  'any',
  'are',
  'as',
  'at',
  'be',
  'been',
  'but',
  'by',
  'did',
  'do',
  'does',
  'for',
  'from',
  'get',
  'got',
  'has',
  'have',
  'his',
  'i',
  'if',
  'in',
  'is',
  'it',
  'its',
  'me',
  'my',
  'of',
  'on',
  'or',
  'our',
  'out',
  'so',
  'that',
  'the',
  'their',
  'them',
  'then',
  'there',
  'they',
  'this',
  'to',
  'up',
  'us',
  'was',
  'we',
  'were',
  'what',
  'when',
  'where',
  'which',
  'who',
  'will',
  'with',
  'you',
  'your',
]);

/**
 * Typed word or phrase → words the topics actually use.
 *
 * Grouped by the part of the job the reader is thinking about. Every group
 * below was seeded either from the domain glossary or from the pre-MiniSearch
 * trigger list this replaced (the "legacy" entries at the end) — no entry is
 * invented vocabulary.
 */
export const HELP_ALIASES: Readonly<Record<string, readonly string[]>> = {
  // ── Change orders: three names for one thing ─────────────────────────────
  // A PCO is Coffman's bucket, the MOD is Coffman's number for it, the FWA is
  // Hoffman's number for the same approval. Readers type whichever one their
  // last email used.
  pco: ['change order', 'funding', 'mod', 'fwa'],
  pcos: ['change order', 'funding'],
  'change order': ['pco', 'mod', 'fwa'],
  'change orders': ['pco', 'mod', 'fwa'],
  co: ['pco', 'change order'],
  mod: ['modification', 'pco', 'fwa'],
  mods: ['modification', 'pco', 'fwa'],
  'mod number': ['mod', 'pco', 'fwa'],
  modification: ['mod', 'pco'],
  fwa: ['work authorization', 'pco', 'mod'],
  'work authorization': ['fwa', 'pco', 'mod'],
  'field work authorization': ['fwa', 'pco', 'mod'],
  authorization: ['fwa', 'approval'],
  'extra work': ['pco', 'change order'],
  bucket: ['funding', 'approved funding'],
  budget: ['funding', 'approved funding', 'remaining'],
  funded: ['funding', 'approved'],

  // ── Sending to the general contractor ────────────────────────────────────
  hoffman: ['gc', 'general contractor'],
  gc: ['general contractor', 'hoffman'],
  'general contractor': ['gc', 'hoffman'],
  client: ['gc', 'general contractor', 'hoffman'],
  customer: ['gc', 'general contractor', 'hoffman'],
  submit: ['send', 'submission', 'approve'],
  submitted: ['send', 'submission'],
  submitting: ['send', 'submission'],
  approve: ['send', 'approval', 'approved'],
  approved: ['approval', 'send'],
  approval: ['approve', 'approved'],
  send: ['approve', 'submission'],
  sending: ['send', 'approve'],
  resend: ['revised', 'reopen', 'send'],
  revision: ['revised', 'reopen'],
  package: ['submission', 'send', 'attachment'],
  transmit: ['send'],
  email: ['send', 'cover'],
  mail: ['email', 'send', 'cover'],
  recipient: ['gc', 'email', 'send'],

  // ── The send's self-audit ────────────────────────────────────────────────
  // The PM's words for "the portal checked itself and won't let me send" — a
  // state he only meets at the worst possible moment, so the vocabulary has to
  // be generous. Targets are words the topics genuinely use.
  verify: ['verification', 'check', 'send'],
  verified: ['verification', 'check', 'recorded'],
  verification: ['check', 'receipt', 'send'],
  'verification receipt': ['verification', 'receipt', 'send'],
  receipt: ['verification', 'check', 'send'],
  'math checks out': ['verification', 'check', 'send'],
  'checks out': ['verification', 'check'],
  readback: ['verification', 'spreadsheet', 'check'],
  reread: ['verification', 'check', 'transcription'],
  'second opinion': ['verification', 'check'],
  'double check': ['verification', 'check'],
  audit: ['verification', 'check', 'receipt'],
  flagged: ['verification', 'disagrees', 'check'],
  disagrees: ['flagged', 'verification', 'check'],
  disagree: ['flagged', 'verification', 'check'],
  blocked: ['verification', 'check', 'send'],
  'wont send': ['verification', 'check', 'send'],
  'cant send': ['verification', 'check', 'send'],
  acknowledge: ['confirm', 'verification'],
  acknowledgment: ['confirm', 'verification'],
  checkbox: ['confirm', 'acknowledge', 'verification'],
  tick: ['confirm', 'acknowledge', 'checkbox'],

  // ── The spreadsheet ──────────────────────────────────────────────────────
  excel: ['spreadsheet', 'xlsx', 'export'],
  xls: ['spreadsheet', 'xlsx', 'excel'],
  xlsx: ['spreadsheet', 'excel', 'export'],
  spreadsheet: ['xlsx', 'excel', 'export'],
  sheet: ['spreadsheet', 'xlsx', 'excel'],
  sheets: ['spreadsheet', 'xlsx', 'excel'],
  workbook: ['spreadsheet', 'xlsx', 'excel'],
  template: ['spreadsheet', 'xlsx', 'excel'],
  export: ['spreadsheet', 'xlsx', 'download'],
  download: ['export', 'spreadsheet', 'xlsx'],
  attachment: ['spreadsheet', 'xlsx', 'pdf'],
  print: ['spreadsheet', 'export', 'pdf'],

  // ── The money ────────────────────────────────────────────────────────────
  money: ['total', 'amount', 'billing', 'invoice'],
  amount: ['total', 'money', 'billing'],
  total: ['money', 'amount', 'invoice'],
  totals: ['money', 'amount', 'invoice'],
  bill: ['billing', 'invoice', 'rollup'],
  billing: ['bill', 'invoice', 'rollup'],
  invoice: ['billing', 'total', 'rollup'],
  rollup: ['weekly rollup', 'week', 'invoice'],
  rollups: ['weekly rollup', 'week', 'invoice'],
  charge: ['bill', 'rate', 'total'],
  paid: ['approved', 'submitted'],
  pay: ['rate', 'bill', 'total'],
  dollars: ['total', 'money', 'amount'],
  math: ['total', 'calculation', 'rate'],

  // ── Rates and prices ─────────────────────────────────────────────────────
  price: ['rate', 'cost'],
  pricing: ['rate', 'cost', 'billing'],
  cost: ['rate', 'price'],
  rates: ['rate'],
  markup: ['profit', 'margin'],
  profit: ['markup'],
  margin: ['markup'],
  tax: ['equip markup', 'equipment tax'],
  'equipment tax': ['equip markup', 'markup'],
  perdiem: ['per diem'],
  'per diem': ['perdiem', 'diem'],
  diem: ['per diem'],
  subsistence: ['per diem'],
  'sunday premium': ['sunday', 'premium'],
  sunday: ['premium', 'sunday premium'],
  overtime: ['ot', 'hours', 'premium'],
  ot: ['overtime', 'hours'],
  swing: ['swing shift', 'shift'],
  shift: ['swing', 'swing shift'],
  nights: ['swing', 'swing shift'],
  'night shift': ['swing shift', 'swing'],

  // ── The daily reports ────────────────────────────────────────────────────
  daily: ['pdf', 'report', 'foreman'],
  report: ['pdf', 'daily', 'foreman'],
  reports: ['pdf', 'daily', 'foreman'],
  pdf: ['report', 'daily'],
  pdfs: ['report', 'daily'],
  foreman: ['pdf', 'report', 'daily'],
  field: ['foreman', 'pdf', 'report'],
  ticket: ['pdf', 'report', 'block'],
  paperwork: ['pdf', 'report'],
  parse: ['parser', 'read'],
  parsed: ['parser', 'read'],

  // ── The inbox and its blocks ─────────────────────────────────────────────
  inbox: ['pdf inbox', 'pending', 'review'],
  pending: ['review', 'inbox', 'waiting'],
  review: ['pending', 'inbox', 'decide'],
  queue: ['inbox', 'pending', 'review'],
  block: ['activity', 'work', 'inbox'],
  blocks: ['activity', 'work', 'inbox'],
  entry: ['block', 'line', 'row'],
  save: ['save to pco', 'saved'],
  saving: ['save', 'saved'],
  skip: ['skipped', 'dismiss'],
  ignore: ['ignored', 'ignored code', 'skip'],
  ignored: ['ignore', 'ignored code'],
  dismiss: ['dismissed', 'skip', 'ignore'],
  'dead code': ['ignored code', 'ignore'],
  hide: ['ignore', 'skip', 'dismiss'],
  duplicate: ['dupe', 'possible duplicate'],
  dupe: ['duplicate', 'possible duplicate'],
  undo: ['reopen', 'revert', 'reopened'],
  reopen: ['reopened', 'revert', 'undo'],
  revert: ['reopen', 'undo'],
  delete: ['remove', 'dismiss', 'void'],
  remove: ['delete', 'dismiss', 'void'],
  code: ['activity code'],
  codes: ['activity code'],
  'cost code': ['activity code'],

  // ── People, machines, materials ──────────────────────────────────────────
  employee: ['roster', 'labor', 'crew'],
  employees: ['roster', 'labor', 'crew'],
  worker: ['employee', 'roster', 'crew', 'labor'],
  workers: ['employee', 'roster', 'crew', 'labor'],
  guy: ['employee', 'roster', 'crew'],
  guys: ['employee', 'roster', 'crew'],
  man: ['employee', 'roster', 'crew'],
  men: ['employee', 'roster', 'crew'],
  person: ['employee', 'roster'],
  people: ['employee', 'roster', 'crew'],
  crew: ['employee', 'roster', 'labor'],
  labor: ['employee', 'hours', 'crew'],
  roster: ['employee', 'equipment'],
  hand: ['employee', 'crew'],
  operator: ['employee', 'crew', 'equipment'],
  equipment: ['machine', 'roster'],
  machine: ['equipment'],
  machines: ['equipment'],
  asset: ['equipment', 'machine'],
  assets: ['equipment', 'machine'],
  truck: ['equipment', 'machine'],
  trucks: ['equipment', 'machine'],
  excavator: ['equipment', 'machine'],
  hoe: ['equipment', 'machine'],
  dozer: ['equipment', 'machine'],
  loader: ['equipment', 'machine'],
  hammer: ['equipment', 'machine', 'rate'],
  'rock hammer': ['equipment', 'machine', 'rate'],
  breaker: ['equipment', 'machine'],
  material: ['materials'],
  materials: ['material'],
  supplies: ['material', 'materials'],
  rental: ['material', 'equipment'],
  sub: ['subcontractor', 'vendor'],
  subs: ['subcontractor', 'vendor'],
  vendor: ['subcontractor', 'vendor invoice support'],
  subcontractor: ['vendor', 'sub'],

  // ── Settings, projects, sign-in ──────────────────────────────────────────
  settings: ['project settings', 'configuration'],
  setup: ['settings', 'project settings'],
  config: ['settings', 'project settings'],
  job: ['project', 'job number'],
  jobs: ['project', 'job number'],
  project: ['job', 'job number'],
  // "link" is deliberately NOT a target here. The sign-in email does contain
  // one, but the corpus is full of labor lines "linked to the roster", and the
  // collision buries the sign-in topic. "sign" is the word that discriminates.
  login: ['sign in', 'sign'],
  signin: ['sign in', 'sign'],
  'log in': ['sign in', 'sign'],
  'logged in': ['sign in', 'sign'],
  'sign in': ['sign', 'magic'],
  password: ['sign in', 'sign', 'magic'],
  account: ['sign in', 'sign', 'user'],
  vista: ['export', 'spreadsheet', 'accounting'],
  heavyjob: ['pdf', 'report', 'foreman', 'job number'],
  'heavy job': ['heavyjob', 'pdf', 'report'],
  hcss: ['heavyjob', 'pdf', 'report'],

  // ── Missing-daily coverage ───────────────────────────────────────────────
  // The card says "coverage" and "site-days quiet"; a PM at a desk types
  // "vacation", "stopped sending", or a man's situation rather than the
  // portal's noun. All targets are words the coverage copy in `inbox.ts` and
  // `troubleshooting.ts` genuinely uses.
  // DELIBERATELY NOT aliased to 'missing'. The table is read backwards as well
  // as forwards (`aliasesForText`), so pointing these at 'missing' would index
  // the coverage sections as being about missing things generally — which is
  // enough to outrank "A PDF never showed up in the Inbox" on the query
  // "recieved pdf missing". One absent report and an absent foreman are
  // different questions with different answers, and 'missing' belongs to the
  // first one.
  coverage: ['foreman', 'daily', 'quiet'],
  // The card's own vocabulary since the two-tier redesign: the PM reads
  // "outlived the recent batches" and may search "batch" or "forwarded".
  batch: ['coverage', 'forwarded', 'foreman'],
  batches: ['batch', 'coverage', 'forwarded'],
  forwarded: ['batch', 'coverage', 'foreman'],
  quiet: ['coverage', 'foreman', 'daily'],
  silent: ['quiet', 'coverage'],
  vacation: ['pause', 'coverage', 'foreman'],
  paused: ['pause', 'coverage', 'watched'],
  'site day': ['coverage', 'quiet', 'foreman'],
  'site days': ['coverage', 'quiet', 'foreman'],
  'stopped sending': ['coverage', 'quiet', 'foreman'],

  // ── Symptom words: what the reader types when something is off ───────────
  wrong: ['incorrect', 'mistake', 'fix'],
  broken: ['fix', 'failed', 'wrong'],
  missing: ['never', 'arrived', 'unknown'],
  lost: ['missing', 'never'],
  stuck: ['blocked', 'failed', 'pending'],
  cant: ['cannot', 'blocked', 'disabled'],
  cannot: ['blocked', 'disabled'],
  wont: ['cannot', 'blocked', 'disabled'],
  fail: ['failed', 'failure'],
  failing: ['failed', 'failure'],
  error: ['failed', 'warning'],
  problem: ['fix', 'warning', 'failed'],
  issue: ['fix', 'warning', 'failed'],
  fix: ['correct', 'mistake'],
  typo: ['fix', 'correct', 'mistake'],
  mistake: ['fix', 'correct', 'wrong'],
  mismatch: ['match', 'wrong', 'differ'],
  flag: ['warning', 'stale'],
  late: ['reopen', 'revised', 'stale'],

  // ── Common misspellings ──────────────────────────────────────────────────
  // Fuzzy matching catches most of these on its own; these are the ones that
  // sit two edits away from the right word, which is past what fuzzy allows on
  // a short term.
  recieve: ['receive'],
  recieved: ['received', 'arrived'],
  reciept: ['receipt'],
  seperate: ['separate'],
  occured: ['occurred'],
  aproove: ['approve'],
  approove: ['approve'],
  aprove: ['approve'],
  spreadsheat: ['spreadsheet'],
  invoce: ['invoice'],
  equiptment: ['equipment'],
  employe: ['employee'],
};

/** Longest key, in words — how far ahead the phrase scanner has to look. */
const MAX_ALIAS_WORDS = Object.keys(HELP_ALIASES).reduce(
  (n, key) => Math.max(n, key.split(' ').length),
  1,
);

/**
 * Extra search terms implied by a query's words.
 *
 * Takes the query's FULL token list, stop words included — "log in" and "sign
 * in" are real phrases here, and dropping "in" first would dissolve them. Stop
 * words are dropped from the OUTPUT instead.
 *
 * Phrases win over their parts: "per diem" expands as a phrase, and its two
 * words are then skipped so "per" cannot drag in unrelated prose. Terms already
 * present literally are never returned — the caller searches those at full
 * weight and these at a discount, so returning one twice would quietly promote
 * it.
 */
export function expandTerms(terms: readonly string[]): string[] {
  const literal = new Set(terms);
  const out = new Set<string>();
  const consumed = new Array<boolean>(terms.length).fill(false);

  const addTargets = (key: string) => {
    for (const target of HELP_ALIASES[key] ?? []) {
      for (const word of target.split(' ')) {
        if (word === '' || literal.has(word) || HELP_STOP_WORDS.has(word)) continue;
        out.add(word);
      }
    }
  };

  for (let width = Math.min(MAX_ALIAS_WORDS, terms.length); width >= 1; width--) {
    for (let i = 0; i + width <= terms.length; i++) {
      if (consumed.slice(i, i + width).some(Boolean)) continue;
      const key = terms.slice(i, i + width).join(' ');
      if (!(key in HELP_ALIASES)) continue;
      addTargets(key);
      // Only a multi-word hit consumes its words. A single-word alias must not
      // stop its neighbours from forming a phrase with it later — and at
      // width 1 there is no "later" anyway.
      if (width > 1) for (let j = i; j < i + width; j++) consumed[j] = true;
    }
  }

  return [...out];
}

/** Whole word, or that word pluralized — "total" reaches "totals", not "totally". */
function saysWord(padded: string, word: string): boolean {
  return (
    padded.includes(` ${word} `) || padded.includes(` ${word}s `) || padded.includes(` ${word}es `)
  );
}

/**
 * The map read backwards: which typed words does this text mean without saying?
 *
 * Returns the alias KEYS whose targets the text contains — "money" for a
 * section that talks about totals — skipping any key the text already says for
 * itself, since the body field covers those.
 *
 * `text` must already be normalized (lowercase, single-spaced, alphanumeric).
 */
export function aliasesForText(text: string): string[] {
  if (text === '') return [];
  const padded = ` ${text} `;
  const keys: string[] = [];
  for (const key of Object.keys(HELP_ALIASES)) {
    if (key.includes(' ') ? padded.includes(` ${key} `) : saysWord(padded, key)) continue;
    const targets = HELP_ALIASES[key] ?? [];
    const hit = targets.some((target) =>
      target.includes(' ') ? padded.includes(` ${target} `) : saysWord(padded, target),
    );
    if (hit) keys.push(key);
  }
  return keys;
}
